import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import Upload from "./pages/Upload";
import VideoDetail from "./pages/VideoDetail";
import NotFound from "./pages/NotFound";
import Search from "./pages/Search";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Profile from "./pages/Profile";
import Playground from "./pages/Playground";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import Roadmap from "./pages/Roadmap";
import YourVideos from "./pages/YourVideos";
import { useAuth } from "@/contexts/AuthContext";

const queryClient = new QueryClient();

// Conditional redirect component based on auth state
const ConditionalRedirect = () => {
  const { user, loading } = useAuth();
  
  // Show nothing while checking auth status
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  // Redirect based on auth status
  return user ? <Navigate to="/dashboard" replace /> : <Navigate to="/" replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/about" element={<About />} />
            
            {/* Protected routes - Dashboard is now at /dashboard */}
            <Route element={<ProtectedRoute redirectPath="/login" />}>
              <Route path="/dashboard" element={<Index />} />
              <Route path="/upload" element={<Upload />} />
              <Route path="/videos" element={<YourVideos />} />
              <Route path="/videos/:id" element={<VideoDetail />} />
              <Route path="/search" element={<Search />} />
              <Route path="/playground" element={<Playground />} />
              <Route path="/profile" element={<Profile />} />
            </Route>
            
            {/* Check auth status and redirect accordingly */}
            <Route path="/redirect" element={<ConditionalRedirect />} />
            
            {/* 404 fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
